import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Layers3, Save } from 'lucide-react';
import { architectApi } from '../../architect/api';
import { ArchitectureEditOptions, ArchitectureEditImpact, ArchitectureEditMode, ArchitectureEditModeButton } from '../../architect/ArchitectureEditMode';
import type { Spec } from '../../architect/types';
import { cleanSpec, deepCopy, pathKind, resolvePath } from '../../architect/utils';
import ContextEditor from '../../architect/ContextEditor';
import StructureTree, { crumbSegments, KIND_DESC } from '../../architect/StructureTree';
import { useWorkspaceDraftGuard } from '../../architect/useWorkspaceDraftGuard';
import { haptic, showConfirm } from '../telegram';
import { Sheet } from './Sheet';
import '../../architect/architect.css';
import './tma-structure.css';

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  spec: Spec | null;
  onChanged: () => void | Promise<void>;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
  initialPath?: string | null;
  architectureApproved?: boolean;
  versionId?: string | null;
  specFingerprint?: string | null;
  baselineLoaded?: boolean;
}

function selectedBlockTitle(value: any, kind: string | null, fallback: string): string {
  const raw = kind === 'meta' ? value?.title : value?.title ?? value?.lines?.[0];
  const title = (Array.isArray(raw) ? raw.join(' ') : String(raw || ''))
    .replace(/<\/?b>/gi, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
  return (kind === 'node' ? title.replace(/^\[[^\]]+\]\s*/, '') : title) || fallback;
}

/** The mobile structure editor reuses the desktop form and its spec cleaner.
 * Each field has the same wire format, validation and versioning as the web
 * editor; this wrapper only changes navigation and presentation for touch.
 */
export function StructureSheet({ open, onClose, projectId, spec, onChanged, onDraftStateChange, initialPath, architectureApproved = false, versionId, specFingerprint, baselineLoaded }: Props) {
  const workingRef = useRef<Spec | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [formRev, setFormRev] = useState(0);
  const [query, setQuery] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [editMode, setEditMode] = useState<ArchitectureEditMode>('wording');
  const [modeChoice, setModeChoice] = useState<ArchitectureEditMode>('wording');
  const [modeChooserOpen, setModeChooserOpen] = useState(false);
  const [structuralConfirm, setStructuralConfirm] = useState(false);
  const approvedBaseline = useRef(false);
  const baselineVersion = useRef<string | null>(null);
  const baselineFingerprint = useRef<string | null>(null);
  const baselineReady = useRef(false);
  const editRevision = useRef(0);
  const lastOpen = useRef(false);
  const latestState = useRef({ dirty, busy: saving });
  latestState.current = { dirty: open && dirty, busy: open && saving };

  useWorkspaceDraftGuard(() => latestState.current, {
    onBusy: () => setError('Finish saving the architecture before leaving.'),
  });

  useEffect(() => {
    onDraftStateChange?.({ dirty: open && dirty, busy: open && saving });
  }, [dirty, open, onDraftStateChange, saving]);

  useEffect(() => {
    if (open && !lastOpen.current) {
      workingRef.current = spec ? deepCopy(spec) : null;
      setSelected(initialPath && spec && pathKind(initialPath) && resolvePath(spec, initialPath) ? initialPath : null);
      setQuery('');
      setFormRev((value) => value + 1);
      setDirty(false);
      setSaving(false);
      setError('');
      setSaved(false);
      setEditMode('wording');
      setModeChoice('wording');
      setModeChooserOpen(false);
      setStructuralConfirm(false);
      approvedBaseline.current = architectureApproved;
      baselineVersion.current = versionId ?? null;
      baselineFingerprint.current = specFingerprint ?? null;
      baselineReady.current = baselineLoaded ?? (versionId !== undefined);
      editRevision.current = 0;
    }
    lastOpen.current = open;
  }, [open, spec, initialPath, architectureApproved, versionId, specFingerprint, baselineLoaded]);

  // A project can finish loading while the sheet is already open. Do not
  // replace a working draft when the parent refetches after a comment/save.
  useEffect(() => {
    if (open && !workingRef.current && spec && !dirty && !saving) {
      workingRef.current = deepCopy(spec);
      setFormRev((value) => value + 1);
    }
  }, [dirty, open, saving, spec]);

  const close = useCallback(async () => {
    if (saving) return;
    if (dirty && !(await showConfirm('Discard unsaved architecture changes?'))) return;
    onDraftStateChange?.({ dirty: false, busy: false });
    haptic.impact('light');
    onClose();
  }, [dirty, onClose, onDraftStateChange, saving]);

  const selectPath = useCallback((path: string) => {
    if (!pathKind(path) || !resolvePath(workingRef.current, path)) return;
    setSelected(path);
    setError('');
    haptic.select();
  }, []);

  const save = useCallback(async (confirmedStructural = false) => {
    if (!workingRef.current || !dirty || saving) return;
    const protectsApprovedWorkflow = approvedBaseline.current || architectureApproved;
    if (protectsApprovedWorkflow && (!baselineReady.current || !baselineFingerprint.current)) {
      setError('Reload the architecture before saving. Your changes are still here.');
      return;
    }
    if (protectsApprovedWorkflow && editMode === 'structural' && !confirmedStructural) {
      setStructuralConfirm(true);
      return;
    }
    setSaving(true);
    setError('');
    const revisionAtSave = editRevision.current;
    try {
      const cleaned = cleanSpec(workingRef.current);
      const savedVersion = protectsApprovedWorkflow
        ? await architectApi.putSpec(projectId, cleaned, {
          edit_mode: editMode,
          expected_version_id: baselineVersion.current,
          expected_spec_fingerprint: baselineFingerprint.current!,
        })
        : baselineReady.current
          ? await architectApi.putSpec(projectId, cleaned, {
            expected_version_id: baselineVersion.current,
            ...(baselineFingerprint.current ? { expected_spec_fingerprint: baselineFingerprint.current } : {}),
          })
          : await architectApi.putSpec(projectId, cleaned);
      if (savedVersion.version?.id) baselineVersion.current = savedVersion.version.id;
      if (savedVersion.spec_fingerprint) baselineFingerprint.current = savedVersion.spec_fingerprint;
      if (protectsApprovedWorkflow && editMode === 'structural') approvedBaseline.current = false;
      if (editRevision.current === revisionAtSave) {
        workingRef.current = deepCopy(cleaned);
        setDirty(false);
        setSaved(true);
        onDraftStateChange?.({ dirty: false, busy: false });
      } else {
        // ContextEditor remains editable while the request is in flight.
        // Never replace its newer working copy with the older saved snapshot.
        setDirty(true);
        setSaved(false);
        setError('The earlier edits were saved. Newer changes remain in your draft — save again when ready.');
      }
      await onChanged();
      haptic.notify('success');
    } catch (cause) {
      setError((cause as Error).message || 'Could not save the architecture. Your changes are still here.');
      haptic.notify('error');
    } finally {
      setSaving(false);
    }
  }, [architectureApproved, dirty, editMode, onChanged, onDraftStateChange, projectId, saving]);

  const chosen = selected && workingRef.current ? resolvePath(workingRef.current, selected) : null;
  const kind = selected ? pathKind(selected) : null;
  const crumbs = selected && workingRef.current ? crumbSegments(workingRef.current, selected) : [];
  const protectsApprovedWorkflow = approvedBaseline.current || architectureApproved;

  return (<>
    <Sheet
      open={open}
      onClose={() => void close()}
      title="Structure"
      className="tma-structure-sheet"
      headerAction={<button type="button" className="tma-sheet-text-btn" onClick={() => void close()} aria-label="Close structure editor">Done</button>}
      footer={
        <div className="tma-structure-footer">
          <span className={'tma-structure-save-status' + (dirty ? ' is-dirty' : '')} aria-live="polite">
            {dirty ? 'Unsaved changes' : saved ? <><Check size={15} /> Saved as a version</> : 'Changes are versioned'}
          </span>
          <button type="button" className="tma-btn tma-btn-primary tma-structure-save" disabled={!dirty || saving} onClick={() => void save()}>
            <Save size={17} /> {saving ? 'Saving…' : protectsApprovedWorkflow ? editMode === 'wording' ? 'Save wording' : 'Save structure' : 'Save changes'}
          </button>
        </div>
      }
    >
      <div className="architect-scope tma-structure-scope">
        <div className="arch-editor tma-structure-editor">
          {error && <div className="tma-structure-error" role="alert">{error}</div>}
          {protectsApprovedWorkflow ? <div className="tma-architecture-edit-options">
            <ArchitectureEditModeButton value={editMode} disabled={saving} onClick={() => { setModeChoice(editMode); setModeChooserOpen(true); }} />
          </div> : null}
          {!workingRef.current ? (
            <div className="tma-structure-empty">Generate an architecture to explore and edit its blocks.</div>
          ) : selected && kind && chosen && chosen.value != null ? (
            <>
              <button type="button" className="tma-structure-back" onClick={() => setSelected(null)}>
                <ArrowLeft size={17} /> All blocks
              </button>
              <div className="tma-structure-context">
                <span className="tma-structure-eyebrow">{crumbs.map((part) => part.label).join(' / ')}</span>
                <h3>{selectedBlockTitle(chosen.value, kind, crumbs[crumbs.length - 1]?.label || 'Edit block')}</h3>
                <p>{KIND_DESC[kind]}</p>
              </div>
              <div className="tma-structure-form" key={selected + ':' + formRev}>
                <ContextEditor
                  workingRef={workingRef}
                  selPath={selected}
                  markDirty={() => { editRevision.current += 1; setDirty(true); setSaved(false); }}
                  bumpForm={() => setFormRev((value) => value + 1)}
                  selectPath={selectPath}
                />
              </div>
            </>
          ) : (
            <>
              <div className="tma-structure-intro">
                <span className="tma-structure-intro-icon"><Layers3 size={20} /></span>
                <span><strong>Architecture blocks</strong><small>Select a case, lane or step to edit.</small></span>
              </div>
              <StructureTree
                workingRef={workingRef}
                formRev={formRev}
                selPath={null}
                onSelect={selectPath}
                onHover={() => undefined}
                query={query}
                setQuery={setQuery}
              />
            </>
          )}
        </div>
      </div>
    </Sheet>
    <Sheet open={open && modeChooserOpen} onClose={() => setModeChooserOpen(false)} title="Edit architecture"
      className="tma-architecture-edit-confirm" footer={<div className="tma-architecture-edit-confirm-actions">
        <button type="button" className="tma-btn tma-btn-outline" onClick={() => setModeChooserOpen(false)}>Cancel</button>
        <button type="button" className="tma-btn tma-btn-primary" onClick={() => { setEditMode(modeChoice); setModeChooserOpen(false); }}>Apply mode</button>
      </div>}>
      <ArchitectureEditOptions value={modeChoice} onChange={setModeChoice} />
      <ArchitectureEditImpact mode={modeChoice} />
    </Sheet>
    <Sheet open={open && structuralConfirm} onClose={() => setStructuralConfirm(false)} title="Save structural changes?"
      className="tma-architecture-edit-confirm" footer={<div className="tma-architecture-edit-confirm-actions">
        <button type="button" className="tma-btn tma-btn-outline" onClick={() => setStructuralConfirm(false)}>Keep editing</button>
        <button type="button" className="tma-btn tma-btn-primary" onClick={() => { setStructuralConfirm(false); void save(true); }}>Save structure</button>
      </div>}>
      <ArchitectureEditOptions value="structural" onChange={() => {}} structuralOnly disabled />
      <ArchitectureEditImpact mode="structural" />
    </Sheet>
  </>);
}

export default StructureSheet;
